import React from 'react';
import { useCopyToClipboard } from 'react-use';
import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownItem,
  IconButton,
  useSnackbar,
} from 'ui-components';
import { EnvelopeIcon } from 'ui-components/dist/EnvelopeIcon';
import { Feature } from '~/components/Feature';
import { FEATURE_FLAGS } from '~/constants';
import { useFetchCreator } from '~/hooks/api/useFetchCreator';
import { ManagerShortType } from '~/types';

interface Props {
  manager: ManagerShortType;
  creatorId?: string; // Accepts creatorId, and fetches manager name and email if not available in `manager`. This provides compatibility with the creators returned from Search API
  title?: string;
  isShared: boolean;
}

export const ManagerDropdown: React.FC<Props> = ({
  creatorId,
  manager,
  title = 'Talent Manager',
  isShared,
}) => {
  const snackbar = useSnackbar();
  const [_state, copyToClipboard] = useCopyToClipboard();
  const { data: creator } = useFetchCreator(
    !manager?.name && !manager?.email ? creatorId : undefined, // If the manager passed does not have email and name, fetch the creator to get the full manager data
  );

  const managerFull = creator?.manager || manager;

  const handleManagerClick = (e: React.MouseEvent, email: string) => {
    e.stopPropagation();

    copyToClipboard(email);

    snackbar.success({
      description: 'Email Copied!',
      duration: 2000,
      isCloseable: true,
    });
  };

  if (!manager) return null;

  return (
    <Dropdown placement="right">
      {({ buttonRef, getReferenceProps }) => (
        <>
          {!isShared ? (
            <DropdownButton>
              <EnvelopeIcon boxSize={4} />
              {title}
            </DropdownButton>
          ) : (
            <Feature name={FEATURE_FLAGS.foamManagerContactShared}>
              <IconButton
                aria-label="Open menu"
                icon={<EnvelopeIcon boxSize={4} />}
                ref={buttonRef}
                size="md"
                variant="transparent"
                {...getReferenceProps()}
              />
            </Feature>
          )}
          <DropdownContent>
            {managerFull ? (
              <DropdownItem
                onClick={(e) => handleManagerClick(e, managerFull.email)}
                trackId="foam-talent-menu-copy-manager-email"
              >
                {/* Default to email for managers that haven't registered yet */}
                <EnvelopeIcon boxSize={4} />{' '}
                {managerFull.name || managerFull.email}
              </DropdownItem>
            ) : null}
          </DropdownContent>
        </>
      )}
    </Dropdown>
  );
};
